import { Inject, Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { ApiResponse } from '../../definitions/api-response.interface';
import { ApiService } from '../../services/api/api.service';
import { LoggerService } from '../../services/logger/logger.service';
import { UserRegistrationStatus } from '../../services/api/definitions';
import { RoutingFunction } from '../../components/auth';
import { SharedUtilService } from '../../services/shared/shared-util.service';
import { ApplicationPath } from '../../services/shared/shared-enums';
import { HttpErrorResponse } from '@angular/common/http';
import { SharedService } from '../../services/shared/shared.service';
import { AppConstants, expAppConstants } from '../../constants/portal-constants';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
	providedIn: 'root'
})
export class AuthGuard implements CanActivate 
{
	constructor(private apiService: ApiService, private loggerService: LoggerService, private sharedUtilService: SharedUtilService, private router: Router,  private localStorageService: LocalStorageService,
		private sharedService: SharedService, @Inject(expAppConstants) readonly appConstantsRef: AppConstants, private translate: TranslateService)
	{}
	async canActivate(): Promise<boolean | UrlTree>
	{
		try 
		{
			const status: ApiResponse = await this.getUserRegistrationStatus();
			const responseObject = <UserRegistrationStatus>status.data;
			responseObject.registrationStatus = responseObject.registrationStatus === 'pending_approval' && responseObject.orgAdminPending? 'pending_business_approval' : responseObject.registrationStatus;
			const routingFunction: RoutingFunction | undefined = this.sharedUtilService.getRoutingFunction(responseObject);
			this.setUserStatusInStorage(responseObject);
			return new Promise((resolve) => 
			{
				if (routingFunction !== undefined) 
				{
					if (this.sharedUtilService.isTmhCorporateAdminUser(responseObject) || !this.checkRegistrationStatus(responseObject)) 
					{
						this.router.navigate([ApplicationPath.authentication]);
						resolve(false);
					}
					else if (this.checkRegistrationStatus(responseObject) && !this.sharedUtilService.isTmhCorporateAdminUser(responseObject)) 
					{
						resolve(true);
					}
				}
			});
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any	
		catch (error: any) 
		{
			return new Promise((resolve) => 
			{
				this.redirectOnError(error);
				resolve(false);
			});
		}
	}
	checkRegistrationStatus(responseObject: UserRegistrationStatus)
	{
		return !!(responseObject.hasOwnProperty('registrationStatus') && responseObject.registrationStatus !== undefined && [ null, 'pending_approval', 'pending_business_approval', 'in_progress' ].includes(responseObject.registrationStatus));
	}
	private async getUserRegistrationStatus(): Promise<ApiResponse> 
	{
		try 
		{
			return await this.apiService.getUserRegistrationStatus();
		}
		catch (error) 
		{
			this.loggerService.log('Error redirecting to logout', error);
			throw error;
		}
	}

	private redirectOnError(error: HttpErrorResponse) 
	{
		const errorMessage =
            error?.error?.statusCode === 401
            	? error.error.message
            	: this.appConstantsRef.messages.filter(
            		(x) => x.category === 'Error' && x.tags.includes('default')
            	)[0]?.value;
		this.sharedUtilService.triggerErrorService(errorMessage, error?.error?.statusCode, error?.error?.id);
		this.sharedService.navigateMethod(this.router, ApplicationPath.unAuthError);
	}
	setUserStatusInStorage(responseObject: UserRegistrationStatus)
	{
		if(responseObject.hasOwnProperty('registrationStatus') && (responseObject.registrationStatus === undefined || [ null, 'approved', 'pending_approval', 'pending_business_approval', 'in_progress' ].includes(responseObject.registrationStatus)))
			this.localStorageService.setRegistrationStatus(responseObject.registrationStatus ?? 'loggedIn');
	}
}
